import { cn } from "@/lib/utils";
import { statusColorClass } from "@/components/attendance/attendance-cell";
import type { CellStatus, MonthlyCell, MonthlyRow } from "@/lib/types";
import { CATEGORY_LABELS } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";

function cellLabel(cell: MonthlyCell | undefined): string {
  if (!cell || cell.status === "pending") return "—";
  if (cell.status === "L" && cell.late_minutes != null) {
    return `L${cell.late_minutes}`;
  }
  return cell.status;
}

function MonthlyCellView({ cell }: { cell: MonthlyCell | undefined }) {
  const label = cellLabel(cell);
  if (label === "—") {
    return <span className="text-slate-300">—</span>;
  }
  const status = (cell?.status ?? "pending") as CellStatus;
  return (
    <span
      className={cn(
        "inline-flex min-w-[1.75rem] items-center justify-center rounded border px-0.5 py-0.5 text-[10px] font-semibold tabular-nums leading-none",
        statusColorClass(status)
      )}
    >
      {label}
    </span>
  );
}

function pctClass(pct: number | null): string {
  if (pct == null) return "text-slate-400";
  if (pct >= 90) return "text-emerald-700";
  if (pct >= 75) return "text-amber-700";
  return "text-red-700";
}

interface MonthlyAttendanceTableProps {
  title: string;
  subtitle?: string;
  dates: string[];
  rows: MonthlyRow[];
  showCategory?: boolean;
  onRowClick?: (row: MonthlyRow) => void;
}

export function MonthlyAttendanceTable({
  title,
  subtitle,
  dates,
  rows,
  showCategory = false,
  onRowClick,
}: MonthlyAttendanceTableProps) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No people in this group.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="sticky left-0 z-10 min-w-[140px] border-r bg-muted/95 px-3 py-2 text-left font-medium">
                  Name
                </th>
                {showCategory && (
                  <th className="sticky left-[140px] z-10 min-w-[72px] border-r bg-muted/95 px-2 py-2 text-left font-medium">
                    Group
                  </th>
                )}
                <th
                  className={cn(
                    "sticky z-10 min-w-[3rem] border-r bg-muted/95 px-1 py-2 text-center text-[10px] font-medium",
                    showCategory ? "left-[212px]" : "left-[140px]"
                  )}
                  title="Present count"
                >
                  P
                </th>
                <th className="min-w-[3rem] px-1 py-2 text-center text-[10px] font-medium">
                  L
                </th>
                <th className="min-w-[3rem] px-1 py-2 text-center text-[10px] font-medium">
                  A
                </th>
                <th
                  className="min-w-[3.5rem] border-r px-1 py-2 text-center text-[10px] font-medium"
                  title="Present % (P+L of accountable days)"
                >
                  %
                </th>
                {dates.map((d) => (
                  <th
                    key={d}
                    className="min-w-[2rem] px-0.5 py-2 text-center text-[10px] font-medium text-muted-foreground"
                    title={d}
                  >
                    {Number(d.slice(8, 10))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.person_id}-${row.category ?? "employee"}`}
                  className={cn(
                    "border-b last:border-0",
                    onRowClick && "cursor-pointer hover:bg-muted/30"
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  <td className="sticky left-0 z-[1] border-r bg-white px-3 py-1.5 font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      {row.person_name}
                      {onRowClick && (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </span>
                  </td>
                  {showCategory && (
                    <td className="sticky left-[140px] z-[1] border-r bg-white px-2 py-1.5">
                      {row.category && (
                        <Badge variant="secondary" className="text-[10px]">
                          {CATEGORY_LABELS[row.category]}
                        </Badge>
                      )}
                    </td>
                  )}
                  <td
                    className={cn(
                      "sticky z-[1] border-r bg-white px-1 py-1 text-center tabular-nums text-xs",
                      showCategory ? "left-[212px]" : "left-[140px]"
                    )}
                  >
                    {row.summary.present}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {row.summary.late}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {row.summary.absent}
                  </td>
                  <td
                    className={cn(
                      "border-r px-1 py-1 text-center text-xs font-semibold tabular-nums",
                      pctClass(row.summary.present_pct)
                    )}
                  >
                    {row.summary.present_pct != null
                      ? `${row.summary.present_pct}%`
                      : "—"}
                  </td>
                  {dates.map((d) => (
                    <td key={d} className="px-0.5 py-1 text-center">
                      <MonthlyCellView cell={row.cells[d]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
